import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useAppSelector } from '@shared/store';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { useAskPageContext } from '@shared/designVariants/useAskPageContext';
import {
  useAgentProfileListControllerAgentProfileListForUsersQuery,
  useUserMeControllerUserMeQuery,
} from '@shared/services/apiService';
import { AgentJobEditor } from './AgentJobEditor';
import { JobAssignmentsDialog } from './JobAssignmentsDialog';
import { jobDetailsPath } from './JobsWorkspace';
import { useWorkspaceJobUsageQuery } from './workspaceApi';
import { useWorkspaceDraftGuard } from './useWorkspaceDraftGuard';
import { useWorkspaceScope } from './useWorkspaceScope';
import { jobSectorLabel, type WorkspaceJobProfile } from './jobMetadata';

export const JobDetailPage = () => {
  const { jobId = '' } = useParams();
  const scope = useWorkspaceScope();
  return <JobDetailSession key={`${scope}:${jobId}`} scope={scope} id={jobId} />;
};

const JobDetailSession = ({ id, scope }: { id: string; scope: string }) => {
  const workspace = useDesignWorkspace();
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const profiles = useAgentProfileListControllerAgentProfileListForUsersQuery(
    { filter: undefined },
    { refetchOnMountOrArgChange: true },
  );
  const me = useUserMeControllerUserMeQuery();
  const usage = useWorkspaceJobUsageQuery({ scope }, { refetchOnMountOrArgChange: true });
  const [tab, setTab] = useState('instructions');
  const [dirty, setDirty] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [assignments, setAssignments] = useState(false);
  const discard = useCallback(() => {
    setDirty(false);
    setEpoch((value) => value + 1);
  }, []);
  const { request, dialog } = useWorkspaceDraftGuard(dirty, discard);
  const profile: WorkspaceJobProfile | undefined = profiles.currentData?.find(
    (item) => item.id === id,
  );
  useAskPageContext(
    profile &&
      !!user?.companyId &&
      !profiles.isError &&
      !me.isError &&
      me.currentData?.companyId === user?.companyId
      ? {
          page: 'Job editor',
          job: { id: profile.id, name: profile.name },
        }
      : null,
  );
  const count = !usage.isError
    ? usage.currentData?.find((item) => item.profileId === id)
    : undefined;
  const canManage =
    !profiles.isError &&
    !me.isError &&
    user?.role !== 'viewer' &&
    me.currentData?.companyId === user?.companyId &&
    !!me.currentData?.privileges.includes('agent.manage');
  const reloadProfiles = profiles.refetch;
  const loadCurrent = useCallback(async () => {
    const result = await reloadProfiles();
    if (result.error) throw new Error('Unable to reload the current Job library.');
    return result.data?.find((item) => item.id === id);
  }, [reloadProfiles, id]);
  if (!profile && profiles.isFetching && !profiles.isError)
    return <CircularProgress aria-label="Loading current job" />;
  if (!profile)
    return (
      <Stack gap={2}>
        <Alert severity="error">This job is unavailable in your current company.</Alert>
        <Button onClick={() => void reloadProfiles()}>Retry current job</Button>
        <Button onClick={() => workspace.open('Jobs')}>Back to Jobs</Button>
      </Stack>
    );
  return (
    <Stack component="section" aria-label="Job details workspace" gap={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
        <Box>
          <Button onClick={() => workspace.open('Jobs')}>Back to Jobs</Button>
          <Typography component="h1" variant="h3" sx={{ mt: 2 }}>
            {profile.name}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {profile.type === 'customer-template'
              ? 'Protected template'
              : profile.type === 'system'
                ? 'Protected system job'
                : 'Reusable custom job'}
          </Typography>
        </Box>
        <Typography role="status" color={dirty ? 'warning.main' : 'text.secondary'}>
          {dirty ? 'Unsaved changes' : 'Saved job'}
        </Typography>
      </Stack>
      {profiles.isError && (
        <Alert severity="warning">
          The latest job could not be loaded. Your draft is retained; retry before saving.{' '}
          <Button onClick={() => void reloadProfiles()}>Retry current job</Button>
        </Alert>
      )}
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        aria-label="Job detail sections"
        variant="scrollable"
        scrollButtons="auto">
        <Tab value="instructions" label="Instructions" />
        <Tab value="workflow" label="Workflow" />
        <Tab value="details" label="Details" />
      </Tabs>
      <Box sx={{ display: tab === 'details' ? 'none' : 'block' }}>
        <AgentJobEditor
          key={`${id}:${epoch}`}
          profile={profile}
          workflow={tab === 'workflow'}
          canManage={canManage}
          loadCurrent={loadCurrent}
          onDirtyChange={setDirty}
          onUpdated={() => {
            void reloadProfiles();
            void usage.refetch();
          }}
          onCopied={(copy) => request(() => workspace.navigate(jobDetailsPath(copy.id)))}
        />
      </Box>
      {tab === 'details' && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack gap={2}>
            <Typography variant="h5">Job details</Typography>
            <Typography>Name: {profile.name}</Typography>
            <Typography>Type: {profile.type}</Typography>
            <Typography>Sector: {jobSectorLabel(profile.sector)}</Typography>
            <Typography>Short description: {profile.summary || 'Not configured'}</Typography>
            <Typography>{profile.taskFlow?.tasks.length || 0} workflow tasks</Typography>
            <Typography>Opening message: {profile.firstMessage || 'Not configured'}</Typography>
            <Typography>
              Opening-message interruptions:{' '}
              {profile.firstMessageInterruptionsEnabled ? 'Allowed' : 'Disabled'}
            </Typography>
            {count ? (
              <>
                <Typography>
                  {count.agentCount} {count.agentCount === 1 ? 'assistant uses' : 'assistants use'}{' '}
                  this job across {count.campaignCount}{' '}
                  {count.campaignCount === 1 ? 'campaign' : 'campaigns'}.
                </Typography>
                <Button onClick={() => setAssignments(true)}>View assigned assistants</Button>
              </>
            ) : (
              <Alert severity={usage.isError ? 'warning' : 'info'}>
                Current usage is unknown. It has not been classified as unused.{' '}
                <Button onClick={() => void usage.refetch()}>Refresh usage</Button>
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              Editing a custom job updates its shared instructions. Agent voice/model choices and
              campaign settings are managed separately.
            </Typography>
          </Stack>
        </Paper>
      )}
      {assignments && (
        <JobAssignmentsDialog id={id} name={profile.name} onClose={() => setAssignments(false)} />
      )}
      {dialog}
    </Stack>
  );
};
